import { action, computed, makeObservable, observable } from "mobx"
import Loader from "reusable/mobx-helpers/Loader"

interface BaseCacheStoreParams<T, K extends keyof T & string> {
   fetchAll: () => Promise<T[]>
   fetchOne: (id: string, orgId?: string) => Promise<T | undefined>
   key: K
}

export default class BaseCacheStore<T extends Record<K, string>, K extends keyof T & string> {
   loader = new Loader(false)
   cache = new Map<string, T>() 

   private fetchAll: () => Promise<T[]>
   private fetchOne: (id: string, orgId?: string) => Promise<T | undefined>
   private key: K

   constructor({ fetchAll, fetchOne, key }: BaseCacheStoreParams<T, K>) {
      this.fetchAll = fetchAll
      this.fetchOne = fetchOne
      this.key = key

      makeObservable(this, {
         cache: observable,  
         allItems: computed,
         fetch: action,      
         updateItem: action,
         removeItem: action,
         setItem: action,
      })
   }

   get allItems(): ReadonlyArray<T> {
      return Array.from(this.cache.values())
   }

   getItem(id: string): T | undefined { 
      return this.cache.get(id) 
   }

   setItem(item: T): void { 
      const id = item[this.key] 
      this.cache.set(id, item)
   }

   removeItem(id: string): void { 
      this.cache.delete(id)
   }

   async fetch() {
      await this.loader.executeLoading(async () => {
         const items = await this.fetchAll()
         this.cache.clear()
         items.forEach(item => this.setItem(item))
      })
   }

   async updateItem(id: string, orgId?: string) {
      const item = await this.fetchOne(id, orgId)
      if (item) {
         this.setItem(item)
      }
   }

   handleWebSocketEvent(event: { type: "add" | "update" | "delete"; id: string; orgId?: string; data?: T }) {
      if (event.type === "delete") {
         this.removeItem(event.id)
      } else if (event.data) {
         this.setItem(event.data)
      } else {
         this.updateItem(event.id, event.orgId)
      }
   }
}
